import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, OverlayTrigger, Pagination, Row, Spinner, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { FiInfo, FiEdit, FiFileText, FiShield, FiDollarSign, FiCreditCard, FiPercent } from 'react-icons/fi';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fileAPI } from '../utils/api';
import { getUser } from '../utils/auth';
import { CHART_COLORS } from '../utils/chartColors';
import './DashboardPage.css';

const POLICY_TYPE_TOP_LIMIT = 8;

const InsuranceMisRowPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityName: PropTypes.string,
  policyType: PropTypes.string,
  policyFrom: PropTypes.string,
  policyTo: PropTypes.string,
  leadInsurer: PropTypes.string,
  coInsurers: PropTypes.string,
  policyNumber: PropTypes.string,
  taxInvoiceNumber: PropTypes.string,
  policyTaxInvoiceSubmissionStatus: PropTypes.string,
  claimsCurrentYearPolicy: PropTypes.string,
  remarks: PropTypes.string,
  sumInsured: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalPremiumPaid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  policyNetPremium: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
  status: PropTypes.string
});

const InsuranceMisAnalyticsPropType = PropTypes.shape({
  totalPolicies: PropTypes.number,
  totalSumInsured: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalPremiumPaid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalNetPremium: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalGst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expiring30: PropTypes.number,
  expiring90: PropTypes.number,
  notTaken: PropTypes.number,
  entityOptions: PropTypes.arrayOf(PropTypes.string),
  insurerOptions: PropTypes.arrayOf(PropTypes.string),
  byEntityData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })),
  byInsurerData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })),
  byPolicyTypeChartData: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.number })),
  normalized: PropTypes.arrayOf(InsuranceMisRowPropType)
});

const InsuranceMisAddFormDataPropType = PropTypes.shape({
  entityName: PropTypes.string,
  policyType: PropTypes.string,
  policyFrom: PropTypes.string,
  policyTo: PropTypes.string,
  sumInsured: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  policyNetPremium: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalPremiumPaid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadInsurer: PropTypes.string,
  coInsurers: PropTypes.string,
  policyNumber: PropTypes.string,
  taxInvoiceNumber: PropTypes.string,
  policyTaxInvoiceSubmissionStatus: PropTypes.string,
  claimsCurrentYearPolicy: PropTypes.string,
  remarks: PropTypes.string
});

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(String(value).replace(/,/g, '').replace(/[^\d.-]/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(toNumber(value));
const formatCompactAmount = (value) => new Intl.NumberFormat('en-IN', { notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 2 }).format(toNumber(value));
const buildTopNWithOthers = (items, limit = 8, key = 'value', name = 'name') => {
  if (!Array.isArray(items) || !items.length) return [];
  const top = items.slice(0, limit);
  const other = items.slice(limit).reduce((sum, item) => sum + (Number(item?.[key]) || 0), 0);
  if (other <= 0) return top;
  return [...top, { [name]: 'Others', [key]: other }];
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDaysToExpiry = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const computeInsuranceMisAnalytics = (rows) => {
  const normalized = rows.map((row) => {
    const entityName = String(row.entityName || 'Unknown').trim() || 'Unknown';
    const leadInsurer = String(row.leadInsurer || 'Unknown').trim() || 'Unknown';
    const policyType = String(row.policyType || 'Unknown').trim() || 'Unknown';
    const policyTo = String(row.policyTo || '').trim() || null;
    const daysToExpiry = getDaysToExpiry(policyTo);
    const isActive = row.isActive === false || row.isActive === 0 || row.isActive === '0' || String(row.isActive).toLowerCase() === 'false'
      ? false
      : true;

    return {
      ...row,
      entityName,
      leadInsurer,
      policyType,
      policyTo,
      isActive,
      sumInsured: toNumber(row.sumInsured),
      totalPremiumPaid: toNumber(row.totalPremiumPaid),
      policyNetPremium: toNumber(row.policyNetPremium),
      gst: toNumber(row.gst),
      daysToExpiry
    };
  });

  // Only consider active rows for KPI totals and aggregations so cards reflect adds/deactivations
  const activeRows = normalized.filter((r) => Boolean(r.isActive) && String(r.status || '').toLowerCase() !== 'deactivated');

  const totalPolicies = activeRows.length;
  const totalSumInsured = activeRows.reduce((s, r) => s + r.sumInsured, 0);
  const totalPremiumPaid = activeRows.reduce((s, r) => s + r.totalPremiumPaid, 0);
  const totalNetPremium = activeRows.reduce((s, r) => s + r.policyNetPremium, 0);
  const totalGst = activeRows.reduce((s, r) => s + r.gst, 0);
  const expiring30 = activeRows.filter((r) => r.daysToExpiry !== null && r.daysToExpiry >= 0 && r.daysToExpiry <= 30).length;
  const expiring90 = activeRows.filter((r) => r.daysToExpiry !== null && r.daysToExpiry >= 0 && r.daysToExpiry <= 90).length;
  const notTaken = activeRows.filter((r) => String(r.policyTo || '').toLowerCase().includes('not yet')).length;

  const byEntity = {};
  const byInsurer = {};
  const bySumInsured = {};
  const byPolicyType = {};
  activeRows.forEach((r) => {
    byEntity[r.entityName] = (byEntity[r.entityName] || 0) + r.totalPremiumPaid;
    byInsurer[r.leadInsurer] = (byInsurer[r.leadInsurer] || 0) + r.totalPremiumPaid;
    bySumInsured[r.entityName] = (bySumInsured[r.entityName] || 0) + r.sumInsured;
    byPolicyType[r.policyType] = (byPolicyType[r.policyType] || 0) + 1;
  });

  const byEntityData = Object.entries(byEntity).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const byInsurerData = Object.entries(byInsurer).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const bySumInsuredData = Object.entries(bySumInsured).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const byPolicyTypeData = Object.entries(byPolicyType).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const bySumInsuredChartData = buildTopNWithOthers(bySumInsuredData, POLICY_TYPE_TOP_LIMIT, 'value', 'name');
  const byPolicyTypeChartData = buildTopNWithOthers(byPolicyTypeData, POLICY_TYPE_TOP_LIMIT, 'value', 'name');

  return {
    normalized,
    totalPolicies,
    totalSumInsured,
    totalPremiumPaid,
    totalNetPremium,
    totalGst,
    expiring30,
    expiring90,
    notTaken,
    byEntityData,
    byInsurerData,
    bySumInsuredData,
    bySumInsuredChartData,
    byPolicyTypeData,
    byPolicyTypeChartData,
    entityOptions: [...new Set(normalized.map((r) => r.entityName))].sort((a, b) => a.localeCompare(b)),
    insurerOptions: [...new Set(normalized.map((r) => r.leadInsurer))].sort((a, b) => a.localeCompare(b))
  };
};

const computeInsuranceMisFilteredRows = ({ normalizedRows, entityFilter, insurerFilter, searchTerm }) => {
  const term = searchTerm.trim().toLowerCase();
  return normalizedRows.filter((row) => {
    if (entityFilter !== 'all' && row.entityName !== entityFilter) return false;
    if (insurerFilter !== 'all' && row.leadInsurer !== insurerFilter) return false;
    if (!term) return true;
    return [
      row.entityName, row.policyType, row.policyNumber, row.leadInsurer, row.taxInvoiceNumber, row.remarks
    ].join(' ').toLowerCase().includes(term);
  });
};

const INITIAL_FORM = {
  entityName: '',
  policyType: '',
  policyFrom: '',
  policyTo: '',
  sumInsured: '',
  policyNetPremium: '',
  gst: '',
  totalPremiumPaid: '',
  leadInsurer: '',
  coInsurers: '',
  policyNumber: '',
  taxInvoiceNumber: '',
  policyTaxInvoiceSubmissionStatus: '',
  claimsCurrentYearPolicy: '',
  remarks: ''
};

const buildFormFromRow = (row = {}) => ({
  entityName: row.entityName || '',
  policyType: row.policyType || '',
  policyFrom: row.policyFrom || '',
  policyTo: row.policyTo || '',
  sumInsured: row.sumInsured ?? '',
  policyNetPremium: row.policyNetPremium ?? '',
  gst: row.gst ?? '',
  totalPremiumPaid: row.totalPremiumPaid ?? '',
  leadInsurer: row.leadInsurer || '',
  coInsurers: row.coInsurers || '',
  policyNumber: row.policyNumber || '',
  taxInvoiceNumber: row.taxInvoiceNumber || '',
  policyTaxInvoiceSubmissionStatus: row.policyTaxInvoiceSubmissionStatus || '',
  claimsCurrentYearPolicy: row.claimsCurrentYearPolicy || '',
  remarks: row.remarks || ''
});

const InsuranceMisSummaryBadges = ({ analytics }) => (
  <Row className="g-3 mb-3">
    <Col md={12} className="d-flex flex-wrap gap-2">
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="ins-tip-expiring-30">Policies with expiry date in next 30 days (inclusive).</BootstrapTooltip>}>
        <Badge bg={analytics.expiring30 ? 'danger' : 'secondary'}>{`Expiring in 30 Days: ${analytics.expiring30}`}</Badge>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="ins-tip-expiring-90">Policies with expiry date in next 90 days (inclusive).</BootstrapTooltip>}>
        <Badge bg={analytics.expiring90 ? 'warning' : 'secondary'} text={analytics.expiring90 ? 'dark' : undefined}>{`Expiring in 90 Days: ${analytics.expiring90}`}</Badge>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="ins-tip-not-taken">Count of rows where Policy To indicates policy not yet taken.</BootstrapTooltip>}>
        <Badge bg={analytics.notTaken ? 'danger' : 'secondary'}>{`Not Yet Taken: ${analytics.notTaken}`}</Badge>
      </OverlayTrigger>
    </Col>
  </Row>
);

InsuranceMisSummaryBadges.propTypes = {
  analytics: InsuranceMisAnalyticsPropType.isRequired
};

const InsuranceMisKpiTile = ({ variant, icon, title, tooltipId, tooltipText, value }) => (
  <Col>
    <Card className={`stat-card stat-${variant} h-100`}>
      <Card.Body className="d-flex align-items-center gap-2">
        <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}>{icon}</div>
        <div>
          <p className="stat-title mb-1 d-flex align-items-center gap-1">
            {title}
            <OverlayTrigger placement="top" overlay={<BootstrapTooltip id={tooltipId}>{tooltipText}</BootstrapTooltip>}>
              <span className="d-inline-flex align-items-center justify-content-center rounded-circle border text-secondary" style={{ width: 16, height: 16, lineHeight: 1 }}>
                <FiInfo size={10} />
              </span>
            </OverlayTrigger>
          </p>
          <h5 className="mb-0 fw-bold">{value}</h5>
        </div>
      </Card.Body>
    </Card>
  </Col>
);

InsuranceMisKpiTile.propTypes = {
  variant: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  tooltipId: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

const InsuranceMisKpis = ({ analytics }) => (
  <Row className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-5">
    <InsuranceMisKpiTile variant="primary" icon={<FiFileText />} title="Total Policies" tooltipId="ins-kpi-total-policies" tooltipText="Total number of policy rows." value={analytics.totalPolicies} />
    <InsuranceMisKpiTile variant="info" icon={<FiShield />} title="Total Sum Insured" tooltipId="ins-kpi-sum-insured" tooltipText="Sum of Sum Insured across all policies." value={formatAmount(analytics.totalSumInsured)} />
    <InsuranceMisKpiTile variant="success" icon={<FiDollarSign />} title="Total Premium Paid" tooltipId="ins-kpi-total-premium" tooltipText="Sum of Total Premium Paid." value={formatAmount(analytics.totalPremiumPaid)} />
    <InsuranceMisKpiTile variant="secondary" icon={<FiCreditCard />} title="Total Policy Net Premium" tooltipId="ins-kpi-net-premium" tooltipText="Sum of Policy Net Premium values." value={formatAmount(analytics.totalNetPremium)} />
    <InsuranceMisKpiTile variant="warning" icon={<FiPercent />} title="Total GST" tooltipId="ins-kpi-gst" tooltipText="Sum of GST values." value={formatAmount(analytics.totalGst)} />
  </Row>
);

InsuranceMisKpis.propTypes = {
  analytics: InsuranceMisAnalyticsPropType.isRequired
};

const InsuranceMisCharts = ({ analytics }) => {
  const topEntityPremiums = useMemo(() => buildTopNWithOthers(analytics.byEntityData || [], 8, 'value', 'name'), [analytics.byEntityData]);
  const topInsurerPremiums = useMemo(() => buildTopNWithOthers(analytics.byInsurerData || [], 8, 'value', 'name'), [analytics.byInsurerData]);
  const topSumInsured = useMemo(() => buildTopNWithOthers(analytics.bySumInsuredData || [], 8, 'value', 'name'), [analytics.bySumInsuredData]);

  return (
    <Row className="g-3 mb-3">
      <Col lg={6}>
        <Card className="shadow-sm h-100">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Premium Paid by Entity (Top 8)</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topEntityPremiums}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Bar dataKey="value" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={6}>
        <Card className="shadow-sm h-100">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Premium Paid by Lead Insurer (Top 8)</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topInsurerPremiums}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Bar dataKey="value" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={6}>
        <Card className="shadow-sm h-100">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Sum Insured by Entity (Top 8)</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topSumInsured}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Bar dataKey="value" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={6}>
        <Card className="shadow-sm h-100">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Policies by Type</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={analytics.byPolicyTypeChartData || []} dataKey="value" nameKey="name" outerRadius={100}>
                  {(analytics.byPolicyTypeChartData || []).map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

InsuranceMisCharts.propTypes = {
  analytics: InsuranceMisAnalyticsPropType.isRequired
};

const InsuranceMisAddEntryForm = ({
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeAddFormField,
  onSubmitAddForm,
  addFormSubmitting,
  submitLabel = 'Save Entry'
}) => (
  <Card.Body>
    {addFormError && <Alert variant="danger" className="mb-3">{addFormError}</Alert>}
    {addFormSuccess && <Alert variant="success" className="mb-3">{addFormSuccess}</Alert>}
    <Form onSubmit={onSubmitAddForm}>
      <Row className="g-3">
        <Col md={4}><Form.Label className="mb-1">Entity</Form.Label><Form.Control value={addFormData.entityName} onChange={(e) => onChangeAddFormField('entityName', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Policy Type</Form.Label><Form.Control value={addFormData.policyType} onChange={(e) => onChangeAddFormField('policyType', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Policy From</Form.Label><Form.Control type="date" value={addFormData.policyFrom} onChange={(e) => onChangeAddFormField('policyFrom', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Policy To</Form.Label><Form.Control type="date" value={addFormData.policyTo} onChange={(e) => onChangeAddFormField('policyTo', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Sum Insured</Form.Label><Form.Control type="number" step="0.01" value={addFormData.sumInsured} onChange={(e) => onChangeAddFormField('sumInsured', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Policy Net Premium</Form.Label><Form.Control type="number" step="0.01" value={addFormData.policyNetPremium} onChange={(e) => onChangeAddFormField('policyNetPremium', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">GST</Form.Label><Form.Control type="number" step="0.01" value={addFormData.gst} onChange={(e) => onChangeAddFormField('gst', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Total Premium Paid</Form.Label><Form.Control type="number" step="0.01" value={addFormData.totalPremiumPaid} onChange={(e) => onChangeAddFormField('totalPremiumPaid', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Lead Insurer</Form.Label><Form.Control value={addFormData.leadInsurer} onChange={(e) => onChangeAddFormField('leadInsurer', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Co-Insurers</Form.Label><Form.Control value={addFormData.coInsurers} onChange={(e) => onChangeAddFormField('coInsurers', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Policy Number</Form.Label><Form.Control value={addFormData.policyNumber} onChange={(e) => onChangeAddFormField('policyNumber', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Tax Invoice Number</Form.Label><Form.Control value={addFormData.taxInvoiceNumber} onChange={(e) => onChangeAddFormField('taxInvoiceNumber', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Submission Status</Form.Label><Form.Control value={addFormData.policyTaxInvoiceSubmissionStatus} onChange={(e) => onChangeAddFormField('policyTaxInvoiceSubmissionStatus', e.target.value)} /></Col>
        <Col md={4}><Form.Label className="mb-1">Claims (Current Year Policy)</Form.Label><Form.Control value={addFormData.claimsCurrentYearPolicy} onChange={(e) => onChangeAddFormField('claimsCurrentYearPolicy', e.target.value)} /></Col>
        <Col md={12}><Form.Label className="mb-1">Remarks</Form.Label><Form.Control value={addFormData.remarks} onChange={(e) => onChangeAddFormField('remarks', e.target.value)} /></Col>
      </Row>
      <div className="mt-3 d-flex justify-content-end"><Button type="submit" disabled={addFormSubmitting}>{addFormSubmitting ? 'Saving...' : submitLabel}</Button></div>
    </Form>
  </Card.Body>
);

InsuranceMisAddEntryForm.propTypes = {
  addFormError: PropTypes.string,
  addFormSuccess: PropTypes.string,
  addFormData: InsuranceMisAddFormDataPropType.isRequired,
  onChangeAddFormField: PropTypes.func.isRequired,
  onSubmitAddForm: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool,
  submitLabel: PropTypes.string
};

const InsuranceMisAddEntryCard = ({
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeAddFormField,
  onSubmitAddForm,
  addFormSubmitting
}) => (
  <Card className="shadow-sm mb-3">
    <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Add Insurance MIS Entry</h5>
      <Button
        size="sm"
        variant={showAddForm ? 'outline-secondary' : 'primary'}
        onClick={() => setShowAddForm((prev) => !prev)}
      >
        {showAddForm ? 'Hide Form' : 'Add Entry'}
      </Button>
    </Card.Header>
    {showAddForm && (
      <InsuranceMisAddEntryForm
        addFormError={addFormError}
        addFormSuccess={addFormSuccess}
        addFormData={addFormData}
        onChangeAddFormField={onChangeAddFormField}
        onSubmitAddForm={onSubmitAddForm}
        addFormSubmitting={addFormSubmitting}
      />
    )}
  </Card>
);

InsuranceMisAddEntryCard.propTypes = {
  showAddForm: PropTypes.bool.isRequired,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string.isRequired,
  addFormSuccess: PropTypes.string.isRequired,
  addFormData: InsuranceMisAddFormDataPropType.isRequired,
  onChangeAddFormField: PropTypes.func.isRequired,
  onSubmitAddForm: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool.isRequired
};

const InsuranceMisHistoryModal = ({
  show,
  onHide,
  loading,
  error,
  historyData,
  policyId,
  entityName
}) => {
  const renderHistoryRows = (rows = []) => (
    rows.map((entry, index) => (
      <tr key={`${entry.id}-${index}`}>
        <td>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}</td>
        <td>{entry.action || '-'}</td>
        <td>{entry.description || '-'}</td>
        <td>{entry.status || '-'}</td>
        <td style={{ wordBreak: 'break-word', maxWidth: 320 }}><pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(entry.changedData || {}, null, 2)}</pre></td>
      </tr>
    ))
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Insurance History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" variant="primary" /><p className="mt-3 mb-0">Loading history...</p></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            <p className="mb-2"><strong>Policy:</strong> {policyId || 'N/A'} {entityName ? `| Entity: ${entityName}` : ''}</p>
            <h6 className="mb-2">Policy Timeline</h6>
            {historyData?.policyHistory?.length ? (
              <div className="table-responsive mb-4">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>{renderHistoryRows(historyData.policyHistory)}</tbody>
                </Table>
              </div>
            ) : (
              <Alert variant="secondary">No policy-specific history available.</Alert>
            )}
            {entityName ? (
              <>
                <h6 className="mb-2">Entity Timeline</h6>
                {historyData?.entityHistory?.length ? (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Action</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>{renderHistoryRows(historyData.entityHistory)}</tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="secondary">No entity timeline history available.</Alert>
                )}
              </>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

InsuranceMisHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  historyData: PropTypes.shape({
    policyHistory: PropTypes.arrayOf(PropTypes.object),
    entityHistory: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  policyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityName: PropTypes.string
};

const InsuranceMisTableCard = ({
  analytics,
  entityFilter,
  onChangeEntityFilter,
  insurerFilter,
  onChangeInsurerFilter,
  searchTerm,
  onChangeSearchTerm,
  filteredRows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  onSetPageSize,
  totalPages,
  onSetCurrentPage,
  canDeleteRows,
  deletingId,
  deactivatingId,
  activatingId,
  onDeleteRow,
  onDeactivateRow,
  onActivateRow,
  onShowHistory,
  onEditRow,
  isAdminRole,
  isEditAllowed
}) => (
  <Card className="shadow-sm">
    <Card.Header className="bg-white border-bottom"><h5 className="mb-0">Insurance MIS Table</h5></Card.Header>
    <Card.Body>
      <Row className="g-2 mb-3">
        <Col md={3}><Form.Select value={entityFilter} onChange={(e) => onChangeEntityFilter(e.target.value)}><option value="all">All Entities</option>{analytics.entityOptions.map((x) => <option key={x} value={x}>{x}</option>)}</Form.Select></Col>
        <Col md={3}><Form.Select value={insurerFilter} onChange={(e) => onChangeInsurerFilter(e.target.value)}><option value="all">All Lead Insurers</option>{analytics.insurerOptions.map((x) => <option key={x} value={x}>{x}</option>)}</Form.Select></Col>
        <Col md={6}><Form.Control value={searchTerm} onChange={(e) => onChangeSearchTerm(e.target.value)} placeholder="Search entity / policy / insurer / invoice / remarks" /></Col>
      </Row>
      {!filteredRows.length ? (
        <p className="text-muted mb-0">No Insurance MIS records found for applied filters.</p>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Entity</th><th>Policy Type</th><th>Policy From</th><th>Policy To</th><th>Lead Insurer</th>
                <th>Policy Number</th><th>Status</th><th>Sum Insured</th><th>Total Premium Paid</th><th>Submission Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, idx) => (
                <tr key={`${row.id}-${idx}`}>
                  <td style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{row.entityName || '-'}</span>
                    {isAdminRole && (
                      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="edit-entity-tooltip">Edit row</BootstrapTooltip>}>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 text-primary"
                          style={{ lineHeight: 1 }}
                          onClick={() => onEditRow(row)}
                          title="Edit row"
                        >
                          <FiEdit size={16} />
                        </Button>
                      </OverlayTrigger>
                    )}
                  </td>
                  <td>{row.policyType || '-'}</td>
                  <td>{row.policyFrom || '-'}</td>
                  <td>{row.policyTo || '-'}</td>
                  <td>{row.leadInsurer || '-'}</td>
                  <td>{row.policyNumber || '-'}</td>
                  <td>
                    {row.isActive === false || String(row.status || '').toLowerCase() === 'deactivated' ? (
                      <Badge bg="dark">Deactivated</Badge>
                    ) : (
                      <Badge bg="success">Active</Badge>
                    )}
                  </td>
                  <td>{formatAmount(row.sumInsured)}</td>
                  <td>{formatAmount(row.totalPremiumPaid)}</td>
                  <td>{row.policyTaxInvoiceSubmissionStatus || '-'}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-2 mb-1"
                      onClick={() => onShowHistory(row)}
                    >
                      History
                    </Button>
                    {row.isActive !== false && isAdminRole ? (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="me-2 mb-1"
                        disabled={deactivatingId === row.id}
                        onClick={() => onDeactivateRow(row)}
                      >
                        {deactivatingId === row.id ? 'Deactivating…' : 'Deactivate'}
                      </Button>
                    ) : null}
                    {(row.isActive === false || String(row.status || '').toLowerCase() === 'deactivated') && isAdminRole ? (
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="me-2 mb-1"
                        disabled={activatingId === row.id}
                        onClick={() => onActivateRow(row)}
                      >
                        {activatingId === row.id ? 'Activating…' : 'Activate'}
                      </Button>
                    ) : null}
                    {canDeleteRows ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deletingId === row.id}
                        onClick={() => onDeleteRow(row)}
                      >
                        {deletingId === row.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <small className="text-muted">Showing {(currentPageSafe - 1) * pageSize + 1}-{Math.min(currentPageSafe * pageSize, filteredRows.length)} of {filteredRows.length}</small>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Rows per page</small>
                <Form.Select size="sm" style={{ width: 90 }} value={pageSize} onChange={(e) => onSetPageSize(Number(e.target.value))}>
                  <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </Form.Select>
              </div>
            </div>
            <Pagination size="sm" className="mb-0">
              <Pagination.First onClick={() => onSetCurrentPage(1)} disabled={currentPageSafe <= 1} />
              <Pagination.Prev onClick={() => onSetCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPageSafe <= 1} />
              <Pagination.Item active>{currentPageSafe}</Pagination.Item>
              <Pagination.Item disabled>{`/ ${totalPages}`}</Pagination.Item>
              <Pagination.Next onClick={() => onSetCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPageSafe >= totalPages} />
              <Pagination.Last onClick={() => onSetCurrentPage(totalPages)} disabled={currentPageSafe >= totalPages} />
            </Pagination>
          </div>
        </div>
      )}
    </Card.Body>
  </Card>
);

InsuranceMisTableCard.propTypes = {
  analytics: InsuranceMisAnalyticsPropType.isRequired,
  entityFilter: PropTypes.string.isRequired,
  onChangeEntityFilter: PropTypes.func.isRequired,
  insurerFilter: PropTypes.string.isRequired,
  onChangeInsurerFilter: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  filteredRows: PropTypes.arrayOf(InsuranceMisRowPropType).isRequired,
  paginatedRows: PropTypes.arrayOf(InsuranceMisRowPropType).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired,
  canDeleteRows: PropTypes.bool,
  deletingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  deactivatingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activatingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeleteRow: PropTypes.func,
  onDeactivateRow: PropTypes.func,
  onActivateRow: PropTypes.func,
  onShowHistory: PropTypes.func,
  onEditRow: PropTypes.func,
  isAdminRole: PropTypes.bool,
  isEditAllowed: PropTypes.bool
};

const InsuranceMisDashboardView = ({
  error,
  canSeePage,
  analytics,
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeAddFormField,
  onSubmitAddForm,
  addFormSubmitting,
  entityFilter,
  onChangeEntityFilter,
  insurerFilter,
  onChangeInsurerFilter,
  searchTerm,
  onChangeSearchTerm,
  filteredRows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  onSetPageSize,
  totalPages,
  onSetCurrentPage,
  canDeleteRows,
  deletingId,
  deactivatingId,
  activatingId,
  onDeleteRow,
  onDeactivateRow,
  onActivateRow,
  onShowHistory,
  onEditRow,
  deactivateError,
  deactivateSuccess,
  activateError,
  activateSuccess,
  editSuccess,
  deleteError,
  deleteSuccess,
  isAdminRole,
  isEditAllowed
}) => {
  if (!canSeePage) {
    return (
      <Container fluid className="dashboard-page py-4">
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning">Insurance MIS is available only for Finance users and EXCO.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      {error && <Alert variant="danger">{error}</Alert>}
      {deleteError && <Alert variant="danger">{deleteError}</Alert>}
      {deactivateError && <Alert variant="danger">{deactivateError}</Alert>}
      {activateError && <Alert variant="danger">{activateError}</Alert>}
      {deleteSuccess && <Alert variant="success">{deleteSuccess}</Alert>}
      {deactivateSuccess && <Alert variant="success">{deactivateSuccess}</Alert>}
      {activateSuccess && <Alert variant="success">{activateSuccess}</Alert>}

      <InsuranceMisSummaryBadges analytics={analytics} />
      <InsuranceMisKpis analytics={analytics} />
      <InsuranceMisCharts analytics={analytics} />

      <InsuranceMisAddEntryCard
        showAddForm={showAddForm}
        setShowAddForm={setShowAddForm}
        addFormError={addFormError}
        addFormSuccess={addFormSuccess}
        addFormData={addFormData}
        onChangeAddFormField={onChangeAddFormField}
        onSubmitAddForm={onSubmitAddForm}
        addFormSubmitting={addFormSubmitting}
      />

      <InsuranceMisTableCard
        analytics={analytics}
        entityFilter={entityFilter}
        onChangeEntityFilter={onChangeEntityFilter}
        insurerFilter={insurerFilter}
        onChangeInsurerFilter={onChangeInsurerFilter}
            searchTerm={searchTerm}
            onChangeSearchTerm={onChangeSearchTerm}
        filteredRows={filteredRows}
        paginatedRows={paginatedRows}
        currentPageSafe={currentPageSafe}
        pageSize={pageSize}
        onSetPageSize={onSetPageSize}
        totalPages={totalPages}
            onSetCurrentPage={onSetCurrentPage}
        canDeleteRows={canDeleteRows}
        deletingId={deletingId}
        deactivatingId={deactivatingId}
            activatingId={activatingId}
            onDeleteRow={onDeleteRow}
            onDeactivateRow={onDeactivateRow}
            onActivateRow={onActivateRow}
        onShowHistory={onShowHistory}
        onEditRow={onEditRow}
        deactivateError={deactivateError}
        deactivateSuccess={deactivateSuccess}
        activateError={activateError}
        activateSuccess={activateSuccess}
            deleteError={deleteError}
            deleteSuccess={deleteSuccess || editSuccess}
        isAdminRole={isAdminRole}
      />
    </Container>
  );
};

InsuranceMisDashboardView.propTypes = {
  error: PropTypes.string.isRequired,
  canSeePage: PropTypes.bool.isRequired,
  analytics: InsuranceMisAnalyticsPropType.isRequired,
  showAddForm: PropTypes.bool.isRequired,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string.isRequired,
  addFormSuccess: PropTypes.string.isRequired,
  addFormData: InsuranceMisAddFormDataPropType.isRequired,
  onChangeAddFormField: PropTypes.func.isRequired,
  onSubmitAddForm: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool.isRequired,
  entityFilter: PropTypes.string.isRequired,
  onChangeEntityFilter: PropTypes.func.isRequired,
  insurerFilter: PropTypes.string.isRequired,
  onChangeInsurerFilter: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  filteredRows: PropTypes.arrayOf(InsuranceMisRowPropType).isRequired,
  paginatedRows: PropTypes.arrayOf(InsuranceMisRowPropType).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired,
  canDeleteRows: PropTypes.bool,
  deletingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  deactivatingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeleteRow: PropTypes.func,
  onDeactivateRow: PropTypes.func,
  onActivateRow: PropTypes.func,
  onShowHistory: PropTypes.func,
  onEditRow: PropTypes.func,
  editSuccess: PropTypes.string,
  deactivateError: PropTypes.string,
  deactivateSuccess: PropTypes.string,
  activateError: PropTypes.string,
  activateSuccess: PropTypes.string,
  deleteError: PropTypes.string,
  deleteSuccess: PropTypes.string,
  isAdminRole: PropTypes.bool,
  isEditAllowed: PropTypes.bool
};

const InsuranceMisDashboardPage = ({ departmentId = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [entityFilter, setEntityFilter] = useState('all');
  const [insurerFilter, setInsurerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormSubmitting, setAddFormSubmitting] = useState(false);
  const [addFormError, setAddFormError] = useState('');
  const [addFormSuccess, setAddFormSuccess] = useState('');
  const [addFormData, setAddFormData] = useState(INITIAL_FORM);
  const [alertRecipients, setAlertRecipients] = useState('');
  const [alertSending, setAlertSending] = useState(false);
  const [alertError, setAlertError] = useState('');
  const [alertSuccess, setAlertSuccess] = useState('');
  const [deleteSubmittingId, setDeleteSubmittingId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [deactivateSubmittingId, setDeactivateSubmittingId] = useState(null);
  const [deactivateError, setDeactivateError] = useState('');
  const [deactivateSuccess, setDeactivateSuccess] = useState('');
  const [activateSubmittingId, setActivateSubmittingId] = useState(null);
  const [activateError, setActivateError] = useState('');
  const [activateSuccess, setActivateSuccess] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyData, setHistoryData] = useState({ policyHistory: [], entityHistory: [], entityName: null, policyId: null });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState(INITIAL_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  const isAdminRole = roleType === 'ADMIN';
  const isEditAllowed = roleType === 'ADMIN' || roleType === 'USER';

  const analytics = useMemo(() => computeInsuranceMisAnalytics(rows), [rows]);

  const filteredRows = useMemo(() => computeInsuranceMisFilteredRows({
    normalizedRows: analytics.normalized,
    entityFilter,
    insurerFilter,
    searchTerm
  }), [analytics.normalized, entityFilter, insurerFilter, searchTerm]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRows.length / pageSize)), [filteredRows.length, pageSize]);
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPageSafe, pageSize]);

  useEffect(() => { setCurrentPage(1); }, [entityFilter, insurerFilter, searchTerm, pageSize]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  const fetchRows = useCallback(async () => {
    if (!departmentId) {
      setError('Department is required to load Insurance MIS.');
      setCanSeePage(true);
      setRows([]);
      return;
    }
    const response = await fileAPI.getInsuranceMisList({ departmentId });
    const nextRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
    setRows(nextRows);
    setCanSeePage(true);
  }, [departmentId]);

  const handleAddFormChange = (field, value) => setAddFormData((prev) => ({ ...prev, [field]: value }));

  const handleEditFormChange = (field, value) => setEditFormData((prev) => ({ ...prev, [field]: value }));

  const openEditRow = (row) => {
    if (!isEditAllowed || !row?.id) return;
    setEditingRow(row);
    setEditFormData(buildFormFromRow(row));
    setEditError('');
    setEditSuccess('');
    setDeleteError('');
    setDeleteSuccess('');
    setAddFormError('');
    setAddFormSuccess('');
  };

  const handleCreateRow = async (event) => {
    event.preventDefault();
    setAddFormError('');
    setAddFormSuccess('');
    setDeleteError('');
    setDeleteSuccess('');
    if (!departmentId) {
      setAddFormError('Department context is required.');
      return;
    }
    try {
      setAddFormSubmitting(true);
      await fileAPI.createInsuranceMisRow({ departmentId, ...addFormData });
      setAddFormSuccess('Insurance MIS entry added successfully.');
      setAddFormData(INITIAL_FORM);
      await fetchRows();
    } catch (err) {
      setAddFormError(err?.message || 'Failed to add Insurance MIS entry.');
    } finally {
      setAddFormSubmitting(false);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!isAdminRole) return;
    const id = row?.id;
    if (!id) return;

    setDeleteError('');
    setDeleteSuccess('');
    setDeactivateError('');
    setDeactivateSuccess('');
    setAddFormError('');
    setAddFormSuccess('');

    const label = row?.policyNumber || row?.policyType || `ID ${id}`;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Delete this Insurance MIS row (${label})? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeleteSubmittingId(id);
      await fileAPI.deleteInsuranceMisRow(id);
      setDeleteSuccess('Insurance MIS row deleted successfully.');
      await fetchRows();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete Insurance MIS row.');
    } finally {
      setDeleteSubmittingId(null);
    }
  };

  const handleShowHistory = async (row) => {
    if (!departmentId || !row?.id) return;
    setHistoryError('');
    setHistoryData({ policyHistory: [], entityHistory: [], entityName: row.entityName || null, policyId: row.id });
    setSelectedHistoryRow(row);
    setShowHistoryModal(true);
    setHistoryLoading(true);

    try {
      const response = await fileAPI.getInsuranceMisHistory({ departmentId, policyId: row.id, entityName: row.entityName });
      setHistoryData(response?.data?.data || { policyHistory: [], entityHistory: [], entityName: row.entityName || null, policyId: row.id });
    } catch (err) {
      setHistoryError(err?.message || 'Failed to load insurance history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeactivateRow = async (row) => {
    if (!isAdminRole) return;
    const id = row?.id;
    if (!id || !departmentId) return;

    setDeactivateError('');
    setDeactivateSuccess('');
    setDeleteError('');
    setDeleteSuccess('');

    const label = row?.policyNumber || row?.policyType || `ID ${id}`;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Deactivate this Insurance MIS row (${label})? This will preserve the record but mark it as inactive.`);
    if (!confirmed) return;

    const reason = window.prompt('Enter deactivation reason (optional):');
    if (reason === null) return;

    try {
      setDeactivateSubmittingId(id);
      await fileAPI.deactivateInsuranceMisRow(id, { departmentId, reason });
      setDeactivateSuccess('Insurance MIS row deactivated successfully.');
      await fetchRows();
    } catch (err) {
      setDeactivateError(err?.message || 'Failed to deactivate Insurance MIS row.');
    } finally {
      setDeactivateSubmittingId(null);
    }
  };

  const handleActivateRow = async (row) => {
    if (!isAdminRole) return;
    const id = row?.id;
    if (!id || !departmentId) return;

    setActivateError('');
    setActivateSuccess('');
    setDeleteError('');
    setDeleteSuccess('');

    const label = row?.policyNumber || row?.policyType || `ID ${id}`;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Activate this Insurance MIS row (${label})? This will mark it as active again.`);
    if (!confirmed) return;

    try {
      setActivateSubmittingId(id);
      await fileAPI.activateInsuranceMisRow(id, { departmentId });
      setActivateSuccess('Insurance MIS row activated successfully.');
      await fetchRows();
    } catch (err) {
      setActivateError(err?.message || 'Failed to activate Insurance MIS row.');
    } finally {
      setActivateSubmittingId(null);
    }
  };

  const handleUpdateRow = async (event) => {
    event.preventDefault();
    if (!isEditAllowed || !editingRow?.id) return;
    setEditError('');
    setEditSuccess('');
    try {
      setEditSubmitting(true);
      await fileAPI.updateInsuranceMisRow(editingRow.id, { departmentId, ...editFormData });
      setEditSuccess('Insurance MIS row updated successfully.');
      await fetchRows();
      setEditingRow(null);
    } catch (err) {
      setEditError(err?.message || 'Failed to update Insurance MIS row.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleSendExpiryAlert = async () => {
    setAlertError('');
    setAlertSuccess('');
    if (!departmentId) {
      setAlertError('Department context is required.');
      return;
    }

    try {
      setAlertSending(true);
      const response = await fileAPI.sendInsuranceMisExpiryAlert({
        departmentId,
        recipients: alertRecipients
      });
      setAlertSuccess(response?.message || 'Expiry alert mail sent.');
    } catch (err) {
      setAlertError(err?.message || 'Failed to send expiry alert mail.');
    } finally {
      setAlertSending(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await fetchRows();
      } catch (err) {
        const message = err?.message || 'Failed to load Insurance MIS.';
        if (message.includes('403')) setCanSeePage(false);
        else setError(message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchRows]);

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3 mb-0">Loading Insurance MIS...</p></div>;
  }

  return (
    <>
      <InsuranceMisDashboardView
        error={error}
        canSeePage={canSeePage}
        analytics={analytics}
        alertRecipients={alertRecipients}
        onChangeAlertRecipients={setAlertRecipients}
        onSendExpiryAlert={handleSendExpiryAlert}
        alertSending={alertSending}
        alertSuccess={alertSuccess}
        alertError={alertError}
        showAddForm={showAddForm}
        setShowAddForm={setShowAddForm}
        addFormError={addFormError}
        addFormSuccess={addFormSuccess}
        addFormData={addFormData}
        onChangeAddFormField={handleAddFormChange}
        onSubmitAddForm={handleCreateRow}
        addFormSubmitting={addFormSubmitting}
        entityFilter={entityFilter}
        onChangeEntityFilter={setEntityFilter}
        insurerFilter={insurerFilter}
        onChangeInsurerFilter={setInsurerFilter}
        searchTerm={searchTerm}
        onChangeSearchTerm={setSearchTerm}
        filteredRows={filteredRows}
        paginatedRows={paginatedRows}
        currentPageSafe={currentPageSafe}
        pageSize={pageSize}
        onSetPageSize={setPageSize}
        totalPages={totalPages}
        onSetCurrentPage={setCurrentPage}
        canDeleteRows={isAdminRole}
        deletingId={deleteSubmittingId}
        deactivatingId={deactivateSubmittingId}
        activatingId={activateSubmittingId}
        onDeleteRow={handleDeleteRow}
        onDeactivateRow={handleDeactivateRow}
        onActivateRow={handleActivateRow}
        onShowHistory={handleShowHistory}
        onEditRow={openEditRow}
        deactivateError={deactivateError}
        deactivateSuccess={deactivateSuccess}
        activateError={activateError}
        activateSuccess={activateSuccess}
        deleteError={deleteError}
        deleteSuccess={deleteSuccess || editSuccess}
        isAdminRole={isAdminRole}
        isEditAllowed={isEditAllowed}
      />

      <InsuranceMisHistoryModal
        show={showHistoryModal}
        onHide={() => {
          setShowHistoryModal(false);
          setHistoryError('');
          setHistoryData({ policyHistory: [], entityHistory: [], entityName: null, policyId: null });
          setSelectedHistoryRow(null);
        }}
        loading={historyLoading}
        error={historyError}
        historyData={historyData}
        policyId={selectedHistoryRow?.id}
        entityName={selectedHistoryRow?.entityName}
      />

      <Modal show={Boolean(editingRow)} onHide={() => setEditingRow(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Insurance MIS Row</Modal.Title>
        </Modal.Header>
        <InsuranceMisAddEntryForm
          addFormError={editError}
          addFormSuccess=""
          addFormData={editFormData}
          onChangeAddFormField={handleEditFormChange}
          onSubmitAddForm={handleUpdateRow}
          addFormSubmitting={editSubmitting}
          submitLabel="Save Changes"
        />
      </Modal>
    </>
  );
};

InsuranceMisDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default InsuranceMisDashboardPage;
